import React, { useState, useEffect, useCallback } from 'react';
import { 
  sessionsService, 
  SessionData, 
  FilterOptions, 
  TeacherOption, 
  SquadOption,
  StudentOption 
} from '../../../shared/services/SessionsService';
import FilterControls from './FilterControls';
import SessionsTable from './SessionsTable';
import SessionDetailsModal from './SessionDetailsModal';
import '../styles/Sessions.css';

const SessionsTab: React.FC = () => {
  const [filteredSessions, setFilteredSessions] = useState<SessionData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterOptions>({});
  const [teachers, setTeachers] = useState<TeacherOption[]>([]);
  const [squads, setSquads] = useState<SquadOption[]>([]);
  const [students, setStudents] = useState<StudentOption[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  const loadSessions = useCallback(async (currentFilters: FilterOptions) => {
    try {
      setError(null);
      const sessionsData = await sessionsService.fetchSessions(currentFilters);
      setFilteredSessions(sessionsData);
    } catch (err) {
      console.error('Error loading sessions:', err);
      setError('Failed to load sessions. Please try again.');
      setFilteredSessions([]);
    }
  }, []);

  // Initialize data on component mount
  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);
      try {
        // Load teachers and squads for filter options
        const [teachersData, squadsData, studentsData] = await Promise.all([
          sessionsService.fetchTeachers(),
          sessionsService.fetchSquads(),
          sessionsService.fetchStudents()
        ]);
        
        setTeachers(teachersData);
        setSquads(squadsData);
        setStudents(studentsData);
        
        // Set default date filter to last 30 days
        const defaultFilters: FilterOptions = {
          dateFrom: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
          dateTo: new Date()
        };
        
        setFilters(defaultFilters);
        
        // Load initial sessions
        await loadSessions(defaultFilters);
      } catch (err) {
        console.error('Error initializing sessions data:', err);
        setError('Failed to load sessions data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, [loadSessions]);

  const handleFiltersChange = useCallback(async (newFilters: FilterOptions) => {
    setFilters(newFilters);
    setLoading(true);
    
    try {
      await loadSessions(newFilters);
    } catch (err) {
      console.error('Error applying filters:', err);
      setError('Failed to apply filters. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [loadSessions]);

  const handleViewFeedback = useCallback((sessionId: string) => {
    setSelectedSessionId(sessionId);
    setShowFeedbackModal(true);
  }, []);

  const handleCloseFeedbackModal = useCallback(() => {
    setShowFeedbackModal(false);
    setSelectedSessionId(null);
  }, []);

  const handleDeleteSession = useCallback(async (sessionId: string) => {
    try {
      await sessionsService.deleteSession(sessionId);
      // Remove the session from local state
      setFilteredSessions(prev => prev.filter(s => s.id !== sessionId));
    } catch (error) {
      console.error('Error deleting session:', error);
      setError('Failed to delete session. Please try again.');
    }
  }, []);

  const refreshData = useCallback(async () => {
    setLoading(true);
    try {
      await loadSessions(filters);
    } finally {
      setLoading(false);
    }
  }, [filters, loadSessions]);

  // Calculate summary statistics
  const summaryStats = React.useMemo(() => {
    if (!filteredSessions.length) {
      return {
        totalSessions: 0,
        averageAttendance: 0,
        averageCompletion: 0,
        onSitePercentage: 0,
        averageRating: 0
      };
    }

    const totalAttendance = filteredSessions.reduce((sum, s) => sum + s.attendanceRate, 0);
    const totalCompletion = filteredSessions.reduce((sum, s) => sum + s.completionPercentage, 0);
    const onSiteCount = filteredSessions.filter(s => s.locationStatus === 'on-site').length;
    const ratingsCount = filteredSessions.filter(s => s.teacherRating).length;
    const totalRating = filteredSessions.reduce((sum, s) => sum + (s.teacherRating || 0), 0);

    return {
      totalSessions: filteredSessions.length,
      averageAttendance: Math.round(totalAttendance / filteredSessions.length),
      averageCompletion: Math.round(totalCompletion / filteredSessions.length),
      onSitePercentage: Math.round((onSiteCount / filteredSessions.length) * 100),
      averageRating: ratingsCount > 0 ? Math.round((totalRating / ratingsCount) * 10) / 10 : 0
    };
  }, [filteredSessions]);

  if (error) {
    return (
      <div className="sessions-tab-error">
        <div className="error-container">
          <h3>⚠️ Error Loading Sessions</h3>
          <p>{error}</p>
          <button onClick={refreshData} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sessions-tab">
      {/* Header with summary stats */}
      <div className="sessions-header">
        <div className="header-content">
          <h2>Session Analytics</h2>
          <button onClick={refreshData} className="refresh-button" disabled={loading}>
            {loading ? '🔄' : '↻'} Refresh
          </button>
        </div>
        
        <div className="summary-stats">
          <div className="stat-card">
            <div className="stat-value">{summaryStats.totalSessions}</div>
            <div className="stat-label">Total Sessions</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{summaryStats.averageAttendance}%</div>
            <div className="stat-label">Avg Attendance</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{summaryStats.averageCompletion}%</div>
            <div className="stat-label">Avg Completion</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{summaryStats.onSitePercentage}%</div>
            <div className="stat-label">On-site Sessions</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">
              {summaryStats.averageRating > 0 ? `${summaryStats.averageRating}/10` : 'N/A'}
            </div>
            <div className="stat-label">Avg Rating</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <FilterControls
        filters={filters}
        onFiltersChange={handleFiltersChange}
        teachers={teachers}
        squads={squads}
        students={students}
        loading={loading}
      />

      {/* Sessions Table */}
      <div className="sessions-content">
        <SessionsTable
          sessions={filteredSessions}
          loading={loading}
          onViewFeedback={handleViewFeedback}
          onDeleteSession={handleDeleteSession}
        />
      </div>

      {/* Feedback Modal */}
      {showFeedbackModal && selectedSessionId && (
        <SessionDetailsModal
          sessionId={selectedSessionId}
          onClose={handleCloseFeedbackModal}
        />
      )}
    </div>
  );
};

export default SessionsTab;